import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Globe, Award } from "lucide-react";

interface Sale {
  id: string;
  domain_name: string;
  price: string;
  platform: string;
  date: string | null;
  sale_type: string | null;
}

const formatPrice = (price: string) => {
  if (!price || price === "—") return price;
  const num = Number(String(price).replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(num) || num === 0) return price;
  if (/^\$|USD/i.test(price)) return price;
  return `$${num.toLocaleString("en-US")}`;
};

const timeAgo = (d: string | null) => {
  if (!d) return "recently";
  try {
    const date = new Date(d);
    const diff = Date.now() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return `${Math.floor(days / 30)} months ago`;
  } catch {
    return "recently";
  }
};

const SocialProofToasts = () => {
  const { pathname } = useLocation();
  const salesRef = useRef<Sale[]>([]);
  const shownIds = useRef<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Skip on admin pages and auth pages
  const isAdminPath = pathname.startsWith("/admin") || pathname === "/login";

  // Fetch recent sales once
  useEffect(() => {
    if (isAdminPath) return;

    const fetchSales = async () => {
      const { data } = await supabase.rpc("get_public_sales");

      if (data) {
        // Take 30 most recent (RPC already orders by date desc) and skip masked prices
        const top = (data as Sale[])
          .filter((s) => s.price && s.price !== "—")
          .slice(0, 30);
        // Deduplicate by domain_name + sale_type
        const seen = new Set<string>();
        const unique = top.filter((s) => {
          const key = `${(s.domain_name || "").trim().toLowerCase()}|${s.sale_type ?? ""}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        salesRef.current = unique;
      }
    };

    fetchSales();
  }, [isAdminPath]);

  const showRandomToast = useCallback(() => {
    const sales = salesRef.current;
    if (sales.length === 0) return;

    // Pick a random sale we haven't shown recently
    let attempts = 0;
    let sale: Sale | undefined;

    while (attempts < sales.length) {
      const idx = Math.floor(Math.random() * sales.length);
      const candidate = sales[idx];
      if (!shownIds.current.has(candidate.id)) {
        sale = candidate;
        break;
      }
      attempts++;
    }

    // If all shown, reset and pick any
    if (!sale) {
      shownIds.current.clear();
      sale = sales[Math.floor(Math.random() * sales.length)];
    }

    if (!sale) return;

    shownIds.current.add(sale.id);
    if (shownIds.current.size > 10) {
      // Prune oldest to keep set small
      const [first] = shownIds.current;
      if (first) shownIds.current.delete(first);
    }

    const isMine = sale.sale_type === "mine";
    const price = formatPrice(sale.price);
    const time = timeAgo(sale.date);

    toast(
      <div className="flex items-start gap-3 min-w-0">
        <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
          isMine ? "bg-emerald-500/15 text-emerald-500" : "bg-primary/15 text-primary"
        }`}>
          {isMine ? <Award className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">
            {sale.domain_name || "Domain"}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Sold for <span className="font-bold text-foreground tabular-nums">{price}</span> on{" "}
            <span className="text-foreground/80">{sale.platform || "—"}</span>
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {time}
          </p>
        </div>
      </div>,
      {
        duration: 6000,
      }
    );
  }, []);

  // Schedule toasts with randomized intervals
  useEffect(() => {
    if (isAdminPath) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // Wait a bit after page load, then start showing
    const initialDelay = 4000 + Math.random() * 6000; // 4-10 seconds

    const scheduleNext = () => {
      const interval = 15000 + Math.random() * 20000; // 15-35 seconds between toasts
      timerRef.current = setTimeout(() => {
        showRandomToast();
        scheduleNext();
      }, interval);
    };

    timerRef.current = setTimeout(() => {
      showRandomToast();
      scheduleNext();
    }, initialDelay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAdminPath, showRandomToast]);

  return null;
};

export default SocialProofToasts;
